import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'gatsby'
import BlockContent from 'src/components/global/BlockContent'
import readingTime from 'reading-time'
import jump from 'jump.js'

import { toPlainText } from 'src/utils/utils'
import { EditorialSectionSerializer } from 'src/utils/editorialSerializer'
import { Image } from 'src/components/image'
import { useMediaQuery } from 'src/hooks/useMediaQuery'
import type { EditorialAuthor } from 'src/components/schema'

import { Twitter, Mail, UpArrow, Facebook } from 'src/components/svgs'

const getAuthorProfilePath = (author: EditorialAuthor) => {
  const title = author.content.main.title.trim()
  const slug = author.content.main.slug.current.trim()
  const isTia = title.toLowerCase() === 'tia' || slug.toLowerCase() === 'tia'

  return !isTia ? `/team/${slug}/` : null
}

export const EditorialInfo = ({
  authors,
  slug,
  title,
  editorialText,
}: {
  authors: Array<EditorialAuthor | null>
  slug: {
    current: string
  }
  title: string
  editorialText: any[]
}) => {
  const stats = readingTime(toPlainText(editorialText))
  const [boxHeight, setBoxHeight] = useState(0)
  const [bottom, setBottom] = useState('100vh')
  const [tocOpen, setTocOpen] = useState(true)
  const ref = useRef<HTMLDivElement>(null)
  const { isMobileDevice } = useMediaQuery()
  const resolvedAuthors = authors.filter((author): author is EditorialAuthor =>
    Boolean(
      author?.content?.main?.title &&
        author.content.main.slug?.current &&
        author.content.main.image?.asset?._id
    )
  )

  useEffect(() => {
    if (isMobileDevice) return

    const timeout = setTimeout(() => {
      if (ref.current) {
        setBoxHeight(ref.current.getBoundingClientRect().height)
      }
    }, 2000)

    return () => clearTimeout(timeout)
  }, [editorialText, isMobileDevice])

  useEffect(() => {
    setTimeout(() => {
      const sharable = require('sharable/browser')
      // eslint-disable-next-line
      const share = sharable.default()
    }, 300)
  }, [0])

  const push = (direction: string) => {
    switch (direction) {
      case 'down':
        setBottom(`calc(100vh - ${boxHeight}px)`)
        break
      case 'up':
        setBottom('100vh')
        break
    }
  }

  return (
    <div className="editorial__info rel br cb z1 bcw p1 m1">
      {resolvedAuthors.length > 0 && (
        <div>
          {resolvedAuthors.map(author => {
            const main = author.content.main
            const profilePath = getAuthorProfilePath(author)

            return (
              <div
                className="editorial__author f jcs aic"
                key={author.id || author._id || main.slug.current}
              >
                <div className="editorial__author-image mr1 rel">
                  <Image
                    className="x abs y"
                    imageId={main.image.asset._id}
                    alt={main.title}
                  />
                </div>
                <p className="m0 p0 ss15">
                  <span>
                    <span className="editorial__author-by">By</span>
                    {profilePath ? (
                      <Link
                        className="editorial__author-link bold"
                        to={profilePath}
                      >
                        {main.title}
                      </Link>
                    ) : (
                      <span className="bold">{main.title}</span>
                    )}
                  </span>
                  {main.position && (
                    <>
                      <br />
                      <span className="ss12 sm15">{main.position}</span>
                    </>
                  )}
                </p>
              </div>
            )
          })}
        </div>
      )}
      <div className="editorial__reading karla ss15 py1">{stats.text}</div>
      <div className="ss15 js-editorial-info">
        <BlockContent
          blocks={editorialText}
          serializers={EditorialSectionSerializer}
        />
      </div>
      <div className="editorial__share pt1 f jcb aic">
        <button
          onClick={() => jump('.js-editorial', { offset: -200 })}
          className="no-style editorial__back cursor ss15 f jcs aic cpink"
        >
          <UpArrow className="mr05" />{' '}
          <span className="underline karla ss15">Back to Top</span>
        </button>
        <div className="cb editorial__social">
          <a
            href="#0"
            data-social="twitter"
            className=""
            data-url={`https://www.asktia.com/article/${slug.current}`}
          >
            <Twitter />
          </a>
          <a
            href="#0"
            data-social="facebook"
            className=""
            data-url={`https://www.asktia.com/article/${slug.current}`}
          >
            <Facebook />
          </a>
          <a
            href={`mailto:?subject=${title}&body=https://www.asktia.com/article/${slug.current}`}
            className=""
          >
            <Mail />
          </a>
        </div>
      </div>
      {!isMobileDevice &&
        (tocOpen ? (
          <div
            className="editorial__share-mobile pt1"
            style={{ bottom: `${bottom}` }}
            onMouseEnter={() => push('down')}
            onMouseLeave={() => push('up')}
          >
            <div className="toc-header f jcb aic px1 py05">
              <span className="bold">Table of contents</span>
              <button
                className="no-style toc-close"
                aria-label="Close TOC"
                onClick={() => setTocOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="ss15 js-editorial-info px1" ref={ref}>
              <BlockContent
                blocks={editorialText}
                serializers={EditorialSectionSerializer}
              />
            </div>
            <div className="p1 f jcb aic">
              <button
                onClick={() => jump('.js-editorial', { offset: -600 })}
                className="no-style editorial__back cursor ss15 f jcs aic cpink"
              >
                <UpArrow className="mr05" />{' '}
                <span className="underline karla ss15">Back to Top</span>
              </button>
              <div className="cb editorial__social">
                <a
                  href="#0"
                  data-social="twitter"
                  className=""
                  data-url={`https://www.asktia.com/article/${slug.current}`}
                >
                  <Twitter />
                </a>
                <a
                  href="#0"
                  data-social="facebook"
                  className=""
                  data-url={`https://www.asktia.com/article/${slug.current}`}
                >
                  <Facebook />
                </a>
                <a
                  href={`mailto:?subject=${title}&body=https://www.asktia.com/article/${slug.current}`}
                  className=""
                >
                  <Mail />
                </a>
              </div>
            </div>
          </div>
        ) : (
          <button
            className="toc-handle"
            aria-label="Show Table of Contents"
            onClick={() => setTocOpen(true)}
          />
        ))}
    </div>
  )
}
